export type AnalyticsEvent =
  | "game_loaded"
  | "engine_load_failed"
  | "mode_selected"
  | "run_started"
  | "run_crashed"
  | "run_completed"
  | "run_abandoned"
  | "near_miss"
  | "flow_ended"
  | "pickup_collected"
  | "nitro_charged"
  | "nitro_activated"
  | "risk_route_taken"
  | "menu_returned";

export type AnalyticsValue = string | number | boolean | null;
export type AnalyticsProperties = Record<string, AnalyticsValue>;

interface SignalClient {
  track(event: string, properties?: AnalyticsProperties): void;
}

declare global {
  interface Window {
    stats?: SignalClient;
  }
}

interface PendingEvent {
  event: AnalyticsEvent;
  properties: AnalyticsProperties;
}

const MAX_PROPERTIES = 20;
const MAX_PENDING_EVENTS = 30;
const pending: PendingEvent[] = [];

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): AnalyticsProperties {
  const entries = Object.entries(properties)
    .filter((entry): entry is [string, AnalyticsValue] => {
      const value = entry[1];
      return (
        value === null ||
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      );
    })
    .slice(0, MAX_PROPERTIES);
  return Object.fromEntries(entries);
}

function flushPending() {
  const client = typeof window === "undefined" ? undefined : window.stats;
  if (!client) return false;
  for (const item of pending.splice(0))
    client.track(item.event, item.properties);
  return true;
}

export function trackEvent(
  event: AnalyticsEvent,
  properties: Record<string, unknown> = {},
) {
  const cleanProperties = sanitizeAnalyticsProperties(properties);
  try {
    if (flushPending()) {
      window.stats?.track(event, cleanProperties);
      return;
    }
    if (pending.length >= MAX_PENDING_EVENTS) pending.shift();
    pending.push({ event, properties: cleanProperties });
  } catch {
    // La télémétrie ne doit jamais perturber le jeu.
  }
}

if (typeof window !== "undefined")
  window.addEventListener("load", flushPending, { once: true });
